import { useEffect, useRef, useState } from 'react'
import {
  openVersantSetup,
  saveVersantPassword,
  type VersantLoginListing,
} from '../services/versantSetup'
import type { VersantRowInput } from '../utils/versantRowStatus'

interface VersantSetupModalProps {
  orgId: string
  clinicId: string
  clinicName: string
  /** The practice's Versant logins as the page last loaded them; null while
   *  loading, 'failed' when they could not be read. */
  listing: VersantLoginListing | 'failed' | null
  /** The clinic's row state. */
  state: VersantRowInput
  onClose: () => void
  /** Something was written (a login made or reused, a password saved) —
   *  the rows reload their states. */
  onChanged: () => void
}

function errorMessage(err: unknown): string {
  return err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : ''
}

type Choice = 'new' | 'reuse'

/**
 * The Versant setup popup (openspec change versant-eligibility-lane,
 * frontend/versant-setup), opened only from a clinic's Versant button. A
 * clinic with no login picks a new one — the mailbox name the admin types,
 * put on the practice's codes domain — or one of the practice's logins to
 * reuse. A clinic with a login sees its address, how to create the Versant
 * user with it and the password field (naming a rejected password when
 * paused as the admin's). Unlinking a clinic is the row's delete button, not
 * this popup. No code is shown: Versant's codes are the robot's alone.
 */
export default function VersantSetupModal({
  orgId, clinicId, clinicName, listing, state, onClose, onChanged,
}: VersantSetupModalProps) {
  const held = listing && listing !== 'failed' ? listing.clinics.find((c) => c.clinicId === clinicId) ?? null : null
  const [address, setAddress] = useState<string | null>(held?.address ?? null)
  const [loginState, setLoginState] = useState<VersantRowInput>(state)
  const [choice, setChoice] = useState<Choice>('new')
  const [mailboxName, setMailboxName] = useState('')
  const [reuseId, setReuseId] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noDomain, setNoDomain] = useState(false)
  const [saved, setSaved] = useState(false)
  const onChangedRef = useRef(onChanged)
  useEffect(() => { onChangedRef.current = onChanged }, [onChanged])

  const domain = listing && listing !== 'failed' ? listing.domain : null
  const reusable = listing && listing !== 'failed' ? listing.logins : []

  async function open() {
    setBusy(true)
    setError(null)
    try {
      const pick = choice === 'reuse' ? { loginId: reuseId } : { mailboxName: mailboxName.trim().toLowerCase() }
      const res = await openVersantSetup(orgId, clinicId, pick)
      if ('noDomain' in res) {
        setNoDomain(true)
      } else {
        setAddress(res.address)
        setLoginState(res.state)
        onChangedRef.current()
      }
    } catch (err: unknown) {
      setError(errorMessage(err) || "This couldn't be saved.")
    } finally {
      setBusy(false)
    }
  }

  async function savePassword() {
    setBusy(true)
    setError(null)
    try {
      setLoginState(await saveVersantPassword(orgId, clinicId, password))
      setPassword('')
      setSaved(true)
      onChangedRef.current()
    } catch (err: unknown) {
      setError(errorMessage(err) || "The password couldn't be saved.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="doctor-modal-overlay" onClick={onClose}>
      <div className="doctor-modal" role="dialog" aria-label="Versant setup" onClick={e => e.stopPropagation()}>
        <div className="doctor-modal__header">
          <div>
            <h3 className="doctor-modal__title">Versant setup</h3>
            <p className="integrations-modal__clinic">{clinicName}</p>
          </div>
          <button className="doctor-modal__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="add-doctor-form">
          {listing === null && !address && <p className="integrations-modal__desc">Getting this clinic&rsquo;s Versant login…</p>}
          {listing === 'failed' && !address && (
            <p className="clinic-save-error" data-testid="versant-setup-failed">The setup couldn&rsquo;t be opened.</p>
          )}
          {noDomain && (
            <p className="clinic-save-error" data-testid="versant-setup-no-domain">
              There&rsquo;s no address for this login yet. Contact VitalRev to finish the setup.
            </p>
          )}

          {listing && listing !== 'failed' && !address && !noDomain && (
            <>
              <div data-testid="versant-setup-intro">
                <p className="integrations-modal__desc">
                  This address becomes the Versant username. VitalRev receives Versant&rsquo;s sign-in codes on it.
                </p>
                <ol className="eyemed-setup__steps">
                  <li>Make the address here.</li>
                  <li>Create a Versant user with it.</li>
                  <li>Enter that user&rsquo;s password at the next screen.</li>
                </ol>
              </div>
              <div className="recent-checks__segmented versant-setup__segmented" role="group" aria-label="Versant login">
                <button
                  type="button"
                  className={`recent-checks__segment ${choice === 'new' ? 'recent-checks__segment--active' : ''}`}
                  aria-pressed={choice === 'new'}
                  onClick={() => setChoice('new')}
                >
                  New address
                </button>
                <button
                  type="button"
                  data-testid="versant-setup-reuse"
                  className={`recent-checks__segment ${choice === 'reuse' ? 'recent-checks__segment--active' : ''}`}
                  aria-pressed={choice === 'reuse'}
                  disabled={reusable.length === 0}
                  title={reusable.length === 0 ? 'No clinic of this practice uses a Versant login yet' : undefined}
                  onClick={() => setChoice('reuse')}
                >
                  Existing address
                </button>
              </div>
              {choice === 'new' ? (
                <>
                  <label className="form-field__label" htmlFor="versant-mailbox">Mailbox name</label>
                  <div className="versant-setup__mailbox">
                    <input
                      id="versant-mailbox"
                      data-testid="versant-setup-mailbox"
                      value={mailboxName}
                      onChange={e => setMailboxName(e.target.value)}
                      placeholder="frontdesk"
                      autoComplete="off"
                      autoFocus
                    />
                    <span className="versant-setup__domain" data-testid="versant-setup-domain">
                      @{domain ?? 'your practice’s codes domain'}
                    </span>
                  </div>
                  <p className="integrations-modal__desc versant-setup__hint">
                    Letters, digits, dots, _ and -. Can&rsquo;t be changed later.
                  </p>
                </>
              ) : (
                <>
                  <label className="form-field__label" htmlFor="versant-reuse">Versant login</label>
                  <select
                    id="versant-reuse" className="form-field__input versant-setup__reuse-pick" data-testid="versant-setup-reuse-pick"
                    value={reuseId} onChange={e => setReuseId(e.target.value)}
                  >
                    <option value="">Choose an address</option>
                    {reusable.map(l => <option key={l.loginId} value={l.loginId}>{l.address}</option>)}
                  </select>
                </>
              )}
            </>
          )}

          {address && (
            <>
              <p className="integrations-modal__desc">
                {loginState === 'paused_vitalrev'
                  ? 'VitalRev is fixing this Versant login. Nothing is needed from you.'
                  : 'In the Versant provider portal, create a user with this username as its email, then enter the password you chose for it.'}
              </p>
              {loginState === 'paused_admin' && (
                <p className="clinic-save-error" data-testid="versant-setup-rejected">
                  Versant could not sign in with this login, so checks are paused. Make sure the Versant user was created with this address, then type its current password to restart them.
                </p>
              )}
              <div className="add-doctor-form__fields">
                <div className="form-field form-field--full">
                  <span className="form-field__label">Username</span>
                  <span className="versant-setup__username" data-testid="versant-setup-address">{address}</span>
                </div>
                {loginState !== 'paused_vitalrev' && (
                  <div className="form-field form-field--full">
                    <label className="form-field__label" htmlFor="versant-password">Password *</label>
                    <input
                      id="versant-password" className="form-field__input" data-testid="versant-setup-password"
                      type="password" value={password} onChange={e => setPassword(e.target.value)}
                      autoComplete="new-password" autoFocus
                      placeholder={loginState === 'set_up' ? 'Saved — type a new one to replace it' : 'Enter password'}
                    />
                  </div>
                )}
              </div>
              {saved && <p className="eyemed-setup__done" data-testid="versant-setup-saved">Password saved.</p>}
            </>
          )}

          {error && <p className="clinic-save-error" data-testid="versant-setup-error">{error}</p>}
          <div className="add-doctor-form__actions eyemed-setup__actions">
            <span />
            <div className="add-doctor-form__actions-right">
              {address ? (
                loginState === 'paused_vitalrev' ? (
                  <button className="add-doctor-form__cancel" onClick={onClose}>Close</button>
                ) : (
                  <button
                    className="check-btn add-doctor-form__submit" data-testid="versant-setup-save"
                    disabled={busy || !password} onClick={() => { void savePassword() }}
                  >
                    Save password
                  </button>
                )
              ) : listing && listing !== 'failed' && !noDomain ? (
                <button
                  className="check-btn add-doctor-form__submit" data-testid="versant-setup-open"
                  disabled={busy || (choice === 'new' ? !mailboxName.trim() : !reuseId)}
                  onClick={() => { void open() }}
                >
                  {choice === 'new' ? 'Make address' : 'Use this login'}
                </button>
              ) : (
                <button className="add-doctor-form__cancel" onClick={onClose}>Close</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
